import { PageLayout } from '../components/layout/PageLayout';

export default function TermsOfService() {
  return (
    <PageLayout>
      <div className="max-w-4xl mx-auto px-4 py-12">
        <h1 className="text-4xl font-bold text-white mb-8">Terms of Service</h1>

        <div className="space-y-6 text-gray-300">
          <p className="text-sm text-gray-400">
            <strong>Effective Date:</strong> November 4, 2025<br />
            <strong>Product:</strong> Social Pack by Throttle Labs<br />
            <strong>Company:</strong> Throttle Labs, Inc. ("Throttle Labs", "we", "us", "our")<br />
            <strong>Website:</strong> <a href="https://throttlelabs.ai" className="text-throttle-cyan-500 hover:underline" target="_blank" rel="noopener noreferrer">https://throttlelabs.ai</a><br />
            <strong>Company Contact:</strong> <a href="mailto:legal@throttlelabs.ai" className="text-throttle-cyan-500 hover:underline">legal@throttlelabs.ai</a> | 624 E Beale St, Kingman, AZ 86401
          </p>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">1) Agreement to terms</h2>
            <p>
              By creating an account, purchasing a plan, or using Social Pack, you agree to these Terms and our Privacy Policy.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">2) Eligibility</h2>
            <p>
              You must be at least 18 and able to form a binding contract. If you use the Service on behalf of a company, you represent you're authorized to bind that company.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">3) Accounts & security</h2>
            <p>
              Provide accurate information, keep credentials confidential, and notify us of unauthorized use. We may suspend or terminate accounts for suspected abuse, fraud, or security risk.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">4) The Service</h2>
            <p>
              Social Pack lets you upload headshots/brand inputs and generate images and related content for marketing and social media. We may modify or discontinue features at any time.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">5) Your Content & license to us</h2>
            <p>
              You retain ownership of Your Content (uploads, prompts, settings, outputs). You grant Throttle Labs a worldwide, non-exclusive, royalty-free license to host, process, reproduce, and display Your Content solely to operate, maintain, secure, and improve the Service (including quality assurance/debugging). We will not use identifiable headshots or saved outputs to market our service without your explicit permission.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">6) Generated outputs & ownership</h2>
            <p>
              As between you and us, you own the outputs generated from your inputs, to the extent permitted by law and subject to third-party rights. You are responsible for ensuring you have necessary rights/consents for any headshots or assets you upload and for how you use the outputs (e.g., likeness/publicity rights, local advertising rules).
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">7) Likeness & consent</h2>
            <p>
              You represent and warrant that you have lawful rights and consent to upload any person's image and to create derivative likenesses. You will not use the Service to create deceptive or harmful content (e.g., deepfakes) without consent or in violation of law.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">8) Output accuracy & model limitations (important)</h2>
            <p className="mb-4">
              AI is probabilistic and imperfect. Social Pack uses machine-learning models that can produce unpredictable or incorrect results, including visual artifacts, misinterpretations, or composition errors.
            </p>
            <p className="mb-4">
              <strong>Accuracy cap.</strong> While we continually improve the Service, we only guarantee up to 60% accuracy for outputs relative to prompts, scene instructions, or brand constraints. This cap supersedes any other oral or written statement.
            </p>
            <p className="mb-4">
              <strong>Human review required.</strong> You agree to review, verify, and edit outputs before use. Do not rely on outputs for legal, regulatory, safety-critical, medical, or financial advice.
            </p>
            <p>
              <strong>No professional advice.</strong> Outputs are for creative/marketing purposes only and do not constitute legal, financial, or compliance advice.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">9) Prohibited uses</h2>
            <p className="mb-4">You agree not to:</p>
            <ul className="list-disc list-inside space-y-2">
              <li>violate laws or third-party rights (privacy, IP, publicity);</li>
              <li>upload illegal, harassing, discriminatory, obscene, or sexually explicit content;</li>
              <li>attempt to extract training data, reverse engineer, or bypass rate limits;</li>
              <li>imply third-party endorsements or affiliations using outputs;</li>
              <li>upload minors' images without lawful consent;</li>
              <li>include logos, trademarks, or identifiable addresses unless you have rights.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">10) Plans, payments, renewals</h2>
            <p>
              Fees, limits, and credits are shown in-app. You authorize our processor (e.g., Stripe) to charge fees and taxes. Subscriptions auto-renew until canceled. Cancel any time in-app; cancellation takes effect at the end of the current term. Except where required by law, fees are non-refundable.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">11) Availability & changes</h2>
            <p>
              We aim for reliable service but do not guarantee uptime. We may change, suspend, or discontinue the Service or features. If we materially reduce paid functionality, we'll provide reasonable notice and options.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">12) Third-party vendors & sub-processors</h2>
            <p>
              We rely on hosting, AI inference, analytics, email, and other vendors as our processors. They act under our instructions. See the Sub-processors page on <a href="https://throttlelabs.ai" className="text-throttle-cyan-500 hover:underline" target="_blank" rel="noopener noreferrer">https://throttlelabs.ai</a>. Some providers may operate outside your country.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">13) Intellectual property</h2>
            <p>
              The Service (software, models, UI, documentation) is owned by Throttle Labs or its licensors. No rights are granted except as expressly stated.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">14) Feedback</h2>
            <p>
              If you provide suggestions, you grant us a perpetual, irrevocable, royalty-free license to use them without restriction.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">15) Copyright/DMCA</h2>
            <p className="mb-4">To report alleged infringement, email <a href="mailto:copyright@throttlelabs.ai" className="text-throttle-cyan-500 hover:underline">copyright@throttlelabs.ai</a> with:</p>
            <ul className="list-disc list-inside space-y-2">
              <li>your contact info;</li>
              <li>identification of the copyrighted work and the alleged infringing material (URL/location);</li>
              <li>a statement of good-faith belief;</li>
              <li>a statement under penalty of perjury;</li>
              <li>your signature.</li>
            </ul>
            <p className="mt-4">We may remove content and terminate repeat infringers.</p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">16) Confidentiality</h2>
            <p>
              Each party may receive non-public information from the other. Both will use it only to perform under these Terms and protect it with reasonable care.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">17) Privacy</h2>
            <p>
              Our Privacy Policy (hosted at <a href="https://throttlelabs.ai" className="text-throttle-cyan-500 hover:underline" target="_blank" rel="noopener noreferrer">https://throttlelabs.ai</a>) explains how we handle personal information. By using the Service, you consent to those practices.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">18) Warranties & disclaimers</h2>
            <p className="uppercase">
              THE SERVICE AND ALL OUTPUTS ARE PROVIDED "AS IS." WE DISCLAIM ALL WARRANTIES, EXPRESS OR IMPLIED, INCLUDING MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT. WITHOUT LIMITING THE FOREGOING, WE DO NOT WARRANT THAT OUTPUTS WILL BE ERROR-FREE, OR THAT THEY WILL MEET YOUR EXPECTATIONS BEYOND THE 60% ACCURACY CAP IN SECTION 8.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">19) Limitation of liability</h2>
            <p className="uppercase">
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, THROTTLE LABS AND ITS SUPPLIERS WILL NOT BE LIABLE FOR INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, EXEMPLARY, OR PUNITIVE DAMAGES, OR FOR LOST PROFITS, REVENUE, OR DATA, EVEN IF ADVISED OF THE POSSIBILITY. OUR TOTAL LIABILITY FOR ANY CLAIM WILL NOT EXCEED THE AMOUNTS YOU PAID IN THE 12 MONTHS BEFORE THE EVENT GIVING RISE TO LIABILITY.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">20) Indemnification</h2>
            <p>
              You will indemnify and hold harmless Throttle Labs and its affiliates from claims, damages, and costs (including reasonable attorneys' fees) arising out of: (a) your use of the Service; (b) Your Content and outputs; (c) your violation of these Terms or law; (d) your violation of third-party rights (including likeness/publicity rights).
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">21) Suspension & termination</h2>
            <p>
              We may suspend or terminate access for violations or risk to the Service. You may terminate by canceling your subscription and deleting your account. Upon termination, your right to use the Service ends; sections intended to survive (IP, Disclaimers, Liability, Indemnity, etc.) will survive.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">22) Governing law & disputes</h2>
            <p className="mb-4">
              <strong>Governing law:</strong> Arizona, excluding conflict-of-laws principles.
            </p>
            <p className="mb-4">
              <strong>Dispute resolution:</strong> Exclusive courts in Mohave County, AZ.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">23) Changes to Terms</h2>
            <p>
              We may update these Terms; changes take effect when posted on <a href="https://throttlelabs.ai" className="text-throttle-cyan-500 hover:underline" target="_blank" rel="noopener noreferrer">https://throttlelabs.ai</a>. For material changes, we'll provide advance notice in-app or via email to your account email.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">24) Contact</h2>
            <p>
              <a href="mailto:legal@throttlelabs.ai" className="text-throttle-cyan-500 hover:underline">legal@throttlelabs.ai</a><br />
              624 E Beale St, Kingman, AZ 86401
            </p>
          </section>
        </div>
      </div>
    </PageLayout>
  );
}
